import React, { useRef, useState } from "react";
import { LayoutGrid, GripVertical } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";

// VIEW: Big | Medium | Small game cards. Medium = the existing Compact card; Small = logo + score + record.
export const cardSizeOf = (getPref, pfx) => (getPref("page_toggle", `${pfx}_card_size`) === "small" ? "small" : getPref("page_toggle", `${pfx}_compact_cards`) === "on" ? "medium" : "big");

const SIZES = [["big", "Big"], ["medium", "Medium"], ["small", "Small"]];
export function CardSizeBtn({ pfx, getPref, setPref, Pop = null }) {
    const cur = cardSizeOf(getPref, pfx);
    const pick = (k) => {
        setPref("page_toggle", `${pfx}_card_size`, k === "small" ? "small" : null);
        setPref("page_toggle", `${pfx}_compact_cards`, k === "medium" ? "on" : null);
    };
    const mobile = typeof window !== "undefined" && window.innerWidth < 1024;
    if (!mobile && Pop) {
        return (
            <Pop k="cardsize" label="View" Icon={LayoutGrid} active={cur !== "big"} color="#2DD4BF">
                <div className="text-[10px] font-black uppercase tracking-wider text-white/50 mb-1.5">Card size</div>
                <div className="flex gap-1" data-testid="access-view-card-size">
                    {SIZES.map(([k, l]) => (
                        <button key={k} type="button" onClick={() => pick(k)} data-testid={`access-view-card-${k}`}
                            className={`h-7 px-2.5 rounded-md border text-[10px] font-bold uppercase transition-colors ${cur === k ? "border-[#2DD4BF] bg-[#2DD4BF]/15 text-[#2DD4BF]" : "border-white/10 bg-white/5 text-white/70 hover:text-white"}`}>{l}</button>
                    ))}
                </div>
            </Pop>
        );
    }
    return (
        <span className="inline-flex items-stretch h-full min-h-[26px] rounded-lg border border-[#2DD4BF]/50 overflow-hidden" data-testid="access-view-card-size">
            <span className="flex items-center gap-1 px-1.5 text-[9px] font-black uppercase tracking-wider text-[#2DD4BF]"><LayoutGrid className="h-3 w-3" />View</span>
            {SIZES.map(([k, l]) => (
                <button key={k} type="button" onClick={() => pick(k)} data-testid={`access-view-card-${k}`}
                    className={`px-1.5 text-[9px] font-black uppercase tracking-wider border-l border-[#2DD4BF]/30 transition-colors ${cur === k ? "bg-[#2DD4BF] text-[#0A0B10]" : "text-white/70 hover:text-white"}`}>{k === "medium" ? "Med" : l}</button>
            ))}
        </span>
    );
}

export const SORT_LEAGUES = [["pwhl", "PWHL", "#9D4CDD"], ["nhl", "NHL", "#8A94A6"], ["international", "WWHC", "#E11D48"], ["ncaa", "NCAA W", "#2563EB"]];
export const allSchedOrder = (getPref) => {
    const o = (getPref("page_toggle", "all_sched_order") || "").split(",").filter((k) => SORT_LEAGUES.some(([x]) => x === k));
    return [...o, ...SORT_LEAGUES.map(([k]) => k).filter((k) => !o.includes(k))];
};
export const allSchedShown = (getPref, league) => {
    const v = getPref("page_toggle", "all_sched_show");
    return v ? v.split(",") : [league || "pwhl"];
};

// SORT: drag (mouse or finger) a league chip to reorder; tap it to show / hide that league.
export function LeagueSortChips({ getPref, setPref, league, onShown = null, single = false }) {
    const { isOwner } = useAuth();
    const order = allSchedOrder(getPref);
    const shown = single ? [league || "pwhl"] : allSchedShown(getPref, league);
    const [drag, setDrag] = useState(null);
    const st = useRef({ k: null, x: 0, y: 0, moved: false });
    const save = (o) => setPref("page_toggle", "all_sched_order", o.join(","));
    const toggle = (k) => {
        const next0 = shown.includes(k) ? shown.filter((x) => x !== k) : [...shown, k];
        const next = next0.length ? next0 : [k];
        setPref("page_toggle", "all_sched_show", next.join(","));
        if (onShown) onShown(order.filter((x) => next.includes(x)));
    };
    const onDown = (k, e) => { st.current = { k, x: e.clientX, y: e.clientY, moved: false }; e.currentTarget.setPointerCapture?.(e.pointerId); };
    const onMove = (e) => {
        const s = st.current;
        if (!s.k) return;
        if (!s.moved && Math.hypot(e.clientX - s.x, e.clientY - s.y) < 8) return;
        s.moved = true; setDrag(s.k);
        const over = document.elementFromPoint(e.clientX, e.clientY)?.closest?.("[data-sort-lg]")?.getAttribute("data-sort-lg");
        if (over && over !== s.k) {
            const o = order.filter((x) => x !== s.k);
            o.splice(o.indexOf(over) + (order.indexOf(over) > order.indexOf(s.k) ? 1 : 0), 0, s.k);
            save(o);
        }
    };
    const onUp = () => { const s = st.current; if (s.k && !s.moved) toggle(s.k); st.current = { k: null }; setDrag(null); };
    return (
        <span className="inline-flex items-stretch h-full min-h-[26px] rounded-lg border border-white/20 overflow-hidden touch-none select-none" data-testid="access-view-all-sort">
            <span className="flex items-center px-1.5 text-[9px] font-black uppercase tracking-wider text-white/70">Show</span>
            {order.filter((k) => k !== "ncaa" || isOwner).map((k) => {
                const [, l, c] = SORT_LEAGUES.find(([x]) => x === k);
                const on = shown.includes(k);
                return (
                    <button key={k} type="button" data-sort-lg={k} onPointerDown={(e) => onDown(k, e)} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
                        title="Tap to show / hide · drag to reorder" data-testid={`all-sort-chip-${k}`}
                        className={`flex items-center gap-0.5 px-1.5 text-[9px] font-black uppercase tracking-wider border-l border-white/15 transition-[background-color,opacity] ${drag === k ? "opacity-60" : ""}`}
                        style={on ? { background: c, color: "#fff" } : { color: "rgba(255,255,255,0.45)" }}>
                        <GripVertical className="h-3 w-3 opacity-60" />{l}
                    </button>
                );
            })}
        </span>
    );
}
